import React, { useRef, useState, useMemo } from 'react';
import { useFrame, useLoader } from '@react-three/fiber';
import { Box, Text, Html, useGLTF, Plane } from '@react-three/drei';
import { TextureLoader } from 'three';
import { useStorefront3DStore } from '../../store/useStorefront3DStore';

const ProductDisplay = ({ item, position = [0, 0, 0] }) => {
  const meshRef = useRef();
  const [hovered, setHovered] = useState(false);
  const [modelError, setModelError] = useState(false);
  const { viewerSettings } = useStorefront3DStore();

  // Load textures and models at top level (hooks must be called unconditionally)
  const imageUrl = item.images?.[0]?.url || '';
  const modelPath = item.model3d || item.glbPath || '';
  
  // Always call hooks with fallback values to avoid conditional calls
  const productTexture = useLoader(TextureLoader, imageUrl || 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==');
  const gltfModel = useGLTF(modelPath || '/models/default-cube.glb');
  
  // Check if we actually have valid data to use
  const hasValidTexture = !!imageUrl;
  const hasValidModel = !!(item.model3d || item.glbPath);

  // Determine product shape based on category
  const getProductShape = () => {
    const category = item.category?.toLowerCase() || '';
    
    if (category.includes('book') || category.includes('magazine')) {
      return { type: 'box', args: [0.8, 0.1, 1.2] };
    } else if (category.includes('bottle') || category.includes('drink')) {
      return { type: 'cylinder', args: [0.3, 1.5, 8] };
    } else if (category.includes('ball') || category.includes('sphere')) {
      return { type: 'sphere', args: [0.5, 16, 16] };
    } else if (category.includes('clothing') || category.includes('shirt')) {
      return { type: 'box', args: [1.2, 0.1, 1.0] };
    } else {
      return { type: 'box', args: [1, 1, 1] };
    }
  };

  const shape = getProductShape();

  // Simple hover animation
  useFrame((state) => {
    if (meshRef.current && hovered) {
      meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 2) * 0.1;
    }
  });

  const handleClick = () => {
    console.log('Product clicked:', item.name);
  };

  // Render 3D model if available
  if (hasValidModel && gltfModel && !modelError) {
    return (
      <group position={position}>
        <primitive
          ref={meshRef}
          object={gltfModel.scene.clone()}
          position={[0, 0.5, 0]}
          scale={[0.5, 0.5, 0.5]}
          onClick={handleClick}
          onPointerOver={() => setHovered(true)}
          onPointerOut={() => setHovered(false)}
          castShadow
          receiveShadow
        />
        {/* Labels and indicators remain the same */}
        {viewerSettings.showLabels && (
          <Html position={[0, 1.8, 0]} center distanceFactor={10} occlude>
            <div style={{
              background: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: `${(viewerSettings.labelSize || 1) * 12}px`,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              transform: 'translate(-50%, -100%)'
            }}>
              {item.name}
              {viewerSettings.showPrices && item.price && (
                <div style={{ fontSize: '10px', opacity: 0.8 }}>
                  ${item.price}
                </div>
              )}
            </div>
          </Html>
        )}
        <Box args={[0.1, item.quantity / 10, 0.1]} position={[1.2, (item.quantity / 10) / 2, 0]}>
          <meshStandardMaterial color={item.quantity > 10 ? '#4caf50' : item.quantity > 5 ? '#ff9800' : '#f44336'} />
        </Box>
      </group>
    );
  }

  return (
    <group position={position}>
      {/* Enhanced Product Display with texture and shape */}
      {shape.type === 'box' && (
        <Box
          ref={meshRef}
          args={shape.args}
          position={[0, shape.args[1] / 2, 0]}
          onClick={handleClick}
          onPointerOver={() => setHovered(true)}
          onPointerOut={() => setHovered(false)}
          castShadow
          receiveShadow
        >
          <meshStandardMaterial 
            map={hasValidTexture ? productTexture : null}
            color={hasValidTexture ? '#ffffff' : (hovered ? '#4fc3f7' : '#90caf9')} 
            transparent
            opacity={item.visible !== false ? 1 : 0.3}
          />
        </Box>
      )}
      
      {shape.type === 'cylinder' && (
        <mesh
          ref={meshRef}
          position={[0, shape.args[1] / 2, 0]}
          onClick={handleClick}
          onPointerOver={() => setHovered(true)}
          onPointerOut={() => setHovered(false)}
          castShadow
          receiveShadow
        >
          <cylinderGeometry args={shape.args} />
          <meshStandardMaterial 
            map={hasValidTexture ? productTexture : null}
            color={hasValidTexture ? '#ffffff' : (hovered ? '#4fc3f7' : '#90caf9')} 
            transparent
            opacity={item.visible !== false ? 1 : 0.3}
          />
        </mesh>
      )}
      
      {shape.type === 'sphere' && (
        <mesh
          ref={meshRef}
          position={[0, shape.args[0], 0]}
          onClick={handleClick}
          onPointerOver={() => setHovered(true)}
          onPointerOut={() => setHovered(false)}
          castShadow
          receiveShadow
        >
          <sphereGeometry args={shape.args} />
          <meshStandardMaterial 
            map={hasValidTexture ? productTexture : null}
            color={hasValidTexture ? '#ffffff' : (hovered ? '#4fc3f7' : '#90caf9')} 
            transparent
            opacity={item.visible !== false ? 1 : 0.3}
          />
        </mesh>
      )}

      {/* Product image as billboard if no texture applied to 3D shape */}
      {!hasValidTexture && imageUrl && (
        <Plane
          args={[0.8, 0.8]}
          position={[0, 1.5, 0.6]}
          rotation={[0, 0, 0]}
        >
          <meshBasicMaterial 
            map={hasValidTexture ? productTexture : null}
            transparent
            opacity={0.9}
          />
        </Plane>
      )}

      {/* Product Label */}
      {viewerSettings.showLabels && (
        <Html
          position={[0, 1.8, 0]}
          center
          distanceFactor={10}
          occlude
        >
          <div
            style={{
              background: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: `${(viewerSettings.labelSize || 1) * 12}px`,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              transform: 'translate(-50%, -100%)'
            }}
          >
            {item.name}
            {viewerSettings.showPrices && item.price && (
              <div style={{ fontSize: '10px', opacity: 0.8 }}>
                ${item.price}
              </div>
            )}
          </div>
        </Html>
      )}

      {/* Stock Level Indicator */}
      <Box
        args={[0.1, item.quantity / 10, 0.1]}
        position={[1.2, (item.quantity / 10) / 2, 0]}
      >
        <meshStandardMaterial 
          color={item.quantity > 10 ? '#4caf50' : item.quantity > 5 ? '#ff9800' : '#f44336'} 
        />
      </Box>
    </group>
  );
};

export default ProductDisplay;
